import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Building2, User, Phone, Mail, MessageSquare, LogOut, ChevronDown, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
  confirmed: { label: 'Bestätigt', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
  completed: { label: 'Abgeschlossen', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
  cancelled: { label: 'Storniert', color: 'bg-red-500/20 text-red-300 border-red-500/30' },
};

const AdminDashboard = ({ user, onLogout }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusDropdown, setStatusDropdown] = useState(null);
  const [notesEdit, setNotesEdit] = useState(null);
  const [notesValue, setNotesValue] = useState('');

  const token = localStorage.getItem('admin_token');

  const fetchAppointments = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/appointments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAppointments(data.appointments || []);
      }
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API_URL}/api/admin/appointments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      setStatusDropdown(null);
      fetchAppointments();
    } catch (e) {
      console.error(e);
    }
  };

  const saveNotes = async (id) => {
    try {
      await fetch(`${API_URL}/api/admin/appointments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: appointments.find(a => a.id === id)?.status || 'new', notes_internal: notesValue }),
      });
      setNotesEdit(null);
      fetchAppointments();
    } catch (e) {
      console.error(e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="border-b border-white/[0.06] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Northlight Admin</h1>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors px-4 py-2 rounded-full border border-white/[0.08] hover:border-white/[0.15]"
          >
            <LogOut className="w-4 h-4" /> Abmelden
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold">Terminbuchungen</h2>
          <span className="text-sm text-gray-500">{appointments.length} Einträge</span>
        </div>

        {loading ? (
          <p className="text-gray-500">Laden...</p>
        ) : appointments.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>Noch keine Terminbuchungen vorhanden.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {appointments.map((appt) => {
              const sc = STATUS_CONFIG[appt.status] || STATUS_CONFIG.new;
              return (
                <div key={appt.id} data-testid={`admin-appointment-${appt.id}`} className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#ff84e4]" />
                        <span className="font-medium">{appt.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-[#d987ff]" />
                        <span className="font-medium">{appt.time} Uhr</span>
                      </div>
                    </div>

                    {/* Status dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setStatusDropdown(statusDropdown === appt.id ? null : appt.id)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${sc.color}`}
                      >
                        {sc.label}
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      {statusDropdown === appt.id && (
                        <div className="absolute right-0 top-full mt-1 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden z-10 min-w-[140px]">
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <button
                              key={key}
                              onClick={() => updateStatus(appt.id, key)}
                              className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/[0.05] transition-colors"
                            >
                              {val.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Building2 className="w-4 h-4 flex-shrink-0" />
                      <span>{appt.company_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <User className="w-4 h-4 flex-shrink-0" />
                      <span>{appt.contact_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <a href={`tel:${appt.phone}`} className="hover:text-white transition-colors">{appt.phone}</a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span>{appt.email || '–'}</span>
                    </div>
                  </div>

                  {appt.message && (
                    <div className="mt-3 flex items-start gap-2 text-sm text-gray-500">
                      <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{appt.message}</span>
                    </div>
                  )}

                  {/* Notes */}
                  <div className="mt-3 pt-3 border-t border-white/[0.04]">
                    {notesEdit === appt.id ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={notesValue}
                          onChange={(e) => setNotesValue(e.target.value)}
                          className="flex-1 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-[#ff84e4]/40"
                          placeholder="Interne Notiz..."
                        />
                        <button onClick={() => saveNotes(appt.id)} className="px-3 py-1.5 text-xs bg-[#ff84e4] text-black rounded-lg font-medium">Speichern</button>
                        <button onClick={() => setNotesEdit(null)} className="px-3 py-1.5 text-xs text-gray-400 border border-white/[0.08] rounded-lg">Abbrechen</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setNotesEdit(appt.id); setNotesValue(appt.notes_internal || ''); }}
                        className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-400 transition-colors"
                      >
                        <FileText className="w-3 h-3" />
                        {appt.notes_internal || 'Notiz hinzufügen'}
                      </button>
                    )}
                  </div>

                  <p className="text-[10px] text-gray-700 mt-2">Erstellt: {new Date(appt.created_at).toLocaleString('de-DE')}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
